import React, { useState } from 'react';
import Joyride, { STATUS, EVENTS } from 'react-joyride';

const GuideTour = ({ run, setRun, onMockAI }) => {
    const [steps] = useState([
        {
            target: 'body',
            content: (
                <div>
                    <h2 className="text-lg font-bold mb-2">Welcome to Resume Architect!</h2>
                    <p>Let's take a quick tour to help you get the most out of the app.</p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
        // ... (other steps unchanged)
        {
            target: '#nav-editor',
            content: 'This is the Editor where you input your details. You can also translate your resume from here!',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-preview',
            content: 'See your resume update in real-time here. Changes in the editor appear here instantly.',
            placement: 'left',
            disableBeacon: true,
            spotlightPadding: 0,
        },
        {
            target: '#nav-ai',
            content: 'Unlock the power of AI! Use this to enhance your writing or fix grammar instantly.',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-job-finder',
            content: (
                <div>
                    <h3 className="font-bold mb-1">New: Job Finder</h3>
                    <p>Search for relevant jobs directly from the app. Use AI to auto-fill search terms based on your resume profile!</p>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-analyze',
            content: (
                <div>
                    <h3 className="font-bold mb-1">Job Analyzer & Premium Tools</h3>
                    <p>Paste a job description to get a match score. Also access premium tools like:</p>
                    <ul className="list-disc ml-4 mt-2 text-sm">
                        <li><b>The Interviewer:</b> Generate custom interview questions.</li>
                        <li><b>The Roaster:</b> Get a brutal (but helpful) critique.</li>
                        <li><b>Cover Letter:</b> Auto-generate a tailored cover letter.</li>
                    </ul>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-templates',
            content: 'Choose from different professional templates to change the look of your resume.',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-translator',
            content: (
                <div>
                    <h3 className="font-bold mb-1">AI Translator</h3>
                    <p>Instantly translate your entire resume into multiple languages with one click.</p>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#btn-smart-assistant',
            content: (
                <div>
                    <h3 className="font-bold mb-1">Smart Assistant</h3>
                    <p>Toggle the assistant sidebar to drag and drop pre-written phrases and skills directly into your resume.</p>
                </div>
            ),
            placement: 'left',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#btn-skill-wand-0, #btn-generate-summary',
            content: (
                <div>
                    <h3 className="font-bold mb-1">Generate with AI</h3>
                    <p>Watch as the AI automatically generates a professional summary based on your skills!</p>
                </div>
            ),
            placement: 'top',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-save-cloud',
            content: (
                <div>
                    <h3 className="font-bold mb-1">Save to Cloud</h3>
                    <p>Securely save your resume to the cloud so you can access it from any device, anytime.</p>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-history',
            content: (
                <div>
                    <h3 className="font-bold mb-1">Resume History</h3>
                    <p>View and load your previously saved resumes. Never lose a version again!</p>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-export',
            content: 'When you are ready, export your resume to PDF here. Remember to remove margins in print settings!',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
        {
            target: '#nav-help',
            content: 'Need to see this tour again? Click here anytime.',
            placement: 'bottom',
            disableBeacon: true,
            spotlightPadding: 4,
        },
    ]);

    const handleJoyrideCallback = (data) => {
        const { status, type, index } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setRun(false);
            localStorage.setItem('hasSeenTour', 'true');
        }

        // Trigger Mock AI when the step is shown (index 9 is the AI step)
        if (type === EVENTS.STEP_AFTER && index === 8) {
            // We are about to enter step 9? No, STEP_AFTER 8 means leaving 8.
            // Joyride doesn't always guarantee order if user clicks back.
            // Better: Use 'step:after' to detect entering? No.
            // Let's use logic: if we just finished step 8 (Smart Assistant), we go to 9.
            if (onMockAI) onMockAI();
        }
        // Actually, trigger it when step 9 becomes active
        if (type === 'step:before' && index === 9) {
            if (onMockAI) onMockAI();
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress
            showSkipButton
            callback={handleJoyrideCallback}
            scrollOffset={100}
            floaterProps={{
                hideArrow: false,
                disableAnimation: false,
            }}
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: '#2563eb', // blue-600
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                buttonNext: {
                    backgroundColor: '#2563eb',
                },
                buttonBack: {
                    marginRight: 10,
                }
            }}
        />
    );
};

export default GuideTour;
